import React, { useState } from "react";
import { Content } from "antd/lib/layout/layout";
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Switch,
  Tag,
  Timeline,
  Typography,
} from "antd";
import {
  DownloadOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { t } from "i18next";
import moment from "moment";

import { getRequest } from "../../axios";
import { ErrorNotificationMsg } from "../../utils/Notifications";
import ButtonField from "../../common/htmlComponents/ButtonField";

const { Text } = Typography;

// Status to Tag color mapping
const STATUS_TAG_COLORS = {
  DELIVERED: "green",
  "FAILED DELIVERY": "red",
  "OUT FOR DELIVERY": "blue",
  "ARRIVED AT HUB": "orange",
  "PICKED UP": "purple",
  "LABEL GENERATED": "cyan",
  "PACKAGE ASSIGNED TO DRIVER": "geekblue",
  "PACKAGE UNASSIGNED FROM DRIVER": "geekblue",
  "ROUTE CHANGED": "geekblue",
  "ROUTE REMOVED": "geekblue",
  "PACKAGE ADDED TO ROUTE": "geekblue",
  "PACKAGE REMOVED FROM ROUTE": "geekblue",
  "RETURN TO DEPO": "volcano",
  "MANUAL STATUS UPDATE": "gold",
  "PHOTO UPLOADED": "blue",
  "SIGNATURE UPLOADED": "green",
};

const getStatusTagColor = (status) => {
  if (!status) return "default";
  const normalizedStatus = String(status).toUpperCase().trim();
  return STATUS_TAG_COLORS[normalizedStatus] || "default";
};

// const formatDateTime = (dateStr) => {
//   if (!dateStr) return "-";
//   const m = moment(dateStr);
//   return m.isValid() ? m.format("DD MMM YYYY – HH:mm A") : dateStr;
// };
const formatDateTime = (dateStr) => {
  return dateStr || "-";
};

/**
 * MOCK SHIPMENT RESPONSE - API Contract for Backend
 * GET api/admin/shipment/{hawb}
 * Share this structure with backend team for API response format
 */
const MOCK_SHIPMENT_RESPONSE = {
  success: true,
  data: {
    hawb: "HC-AHM-2024-001234",
    parcel_no: "HC-AHM-2024-001234",
    company_name: "Acme Logistics Pvt Ltd",
    company: "Acme Logistics Pvt Ltd",
    origin: "Ahmedabad Hub, Gujarat",
    customer_name: "John Doe",
    customer_phone: "1234567890",
    customer_email: "john.doe@example.com",
    customer_code: "1234567890",
    customer_address: "45, MG Road, Near City Mall, Mumbai - 400001",
    shipper_name: "John Doe",
    shipper_address: "45, MG Road, Near City Mall, Mumbai - 400001",
    weight: "2.5 kg",
    price: "100",
    current_route: "Mumbai Central Route A",
    route_name: "Mumbai Central Route A",
    current_driver: "Rajesh Kumar",
    assigned_to: "Rajesh Kumar",
    driver_name: "Rajesh Kumar",
    current_status: {
      status: "OUT FOR DELIVERY",
      timestamp: "2024-02-17T10:30:00Z",
      updated_by: "Rajesh Kumar",
      role: "Driver",
    },
    status: "OUT FOR DELIVERY",
    events: [
      {
        id: 1,
        status: "LABEL GENERATED",
        timestamp: "2024-02-14T09:00:00Z",
        location: "Ahmedabad Sorting Center",
        reason: null,
        driver_name: null,
        old_route: null,
        new_route: null,
        updated_by: "Priya Sharma",
        role: "Admin",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 2,
        status: "PICKED UP",
        timestamp: "2024-02-14T14:30:00Z",
        location: "Acme Logistics Warehouse, Ahmedabad",
        reason: null,
        driver_name: "Amit Patel",
        old_route: null,
        new_route: null,
        updated_by: "Amit Patel",
        role: "Driver",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 3,
        status: "ARRIVED AT HUB",
        timestamp: "2024-02-15T08:00:00Z",
        location: "Mumbai Distribution Hub",
        reason: null,
        driver_name: null,
        old_route: null,
        new_route: null,
        updated_by: "System",
        role: "System",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 4,
        status: "PACKAGE ASSIGNED TO DRIVER",
        timestamp: "2024-02-15T09:15:00Z",
        location: null,
        reason: null,
        driver_name: "Rajesh Kumar",
        old_route: null,
        new_route: "Mumbai Central Route A",
        updated_by: "Depot Manager",
        role: "Depot Manager",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 5,
        status: "PACKAGE ADDED TO ROUTE",
        timestamp: "2024-02-15T09:20:00Z",
        location: null,
        reason: null,
        driver_name: null,
        old_route: null,
        new_route: "Mumbai Central Route A",
        updated_by: "Depot Manager",
        role: "Depot Manager",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 6,
        status: "OUT FOR DELIVERY",
        timestamp: "2024-02-17T10:30:00Z",
        location: "45, MG Road, Mumbai",
        reason: null,
        driver_name: "Rajesh Kumar",
        old_route: null,
        new_route: null,
        updated_by: "Rajesh Kumar",
        role: "Driver",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 7,
        status: "FAILED DELIVERY",
        timestamp: "2024-02-16T15:45:00Z",
        location: "45, MG Road, Mumbai",
        reason: "Customer not available - premises locked",
        driver_name: "Rajesh Kumar",
        old_route: null,
        new_route: null,
        updated_by: "Rajesh Kumar",
        role: "Driver",
        pod_url: null,
        pofd_url: "https://example.com/pofd/sample-pofd.pdf",
      },
      {
        id: 8,
        status: "DELIVERED",
        timestamp: "2024-02-17T11:20:00Z",
        location: "45, MG Road, Mumbai",
        reason: null,
        driver_name: "Rajesh Kumar",
        old_route: null,
        new_route: null,
        updated_by: "Rajesh Kumar",
        role: "Driver",
        pod_url: "https://example.com/pod/sample-pod.pdf",
        pofd_url: null,
      },
      {
        id: 9,
        status: "ROUTE CHANGED",
        timestamp: "2024-02-14T16:00:00Z",
        location: null,
        reason: "Route optimization",
        driver_name: null,
        old_route: "Mumbai North Route",
        new_route: "Mumbai Central Route A",
        updated_by: "Depot Manager",
        role: "Depot Manager",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 10,
        status: "RETURN TO DEPO",
        timestamp: "2024-02-16T18:00:00Z",
        location: "Mumbai Distribution Hub",
        reason: "Re-attempt scheduled for next day",
        driver_name: "Rajesh Kumar",
        old_route: null,
        new_route: null,
        updated_by: "Rajesh Kumar",
        role: "Driver",
        pod_url: null,
        pofd_url: null,
      },
      {
        id: 11,
        status: "MANUAL STATUS UPDATE",
        timestamp: "2024-02-15T10:00:00Z",
        location: null,
        reason: "Customer requested address correction",
        driver_name: null,
        old_route: null,
        new_route: null,
        updated_by: "Support Agent",
        role: "Support",
        pod_url: null,
        pofd_url: null,
      },
      
    ],
  },
};

/**
 * API Response Format - Copy and share with backend team
 * Endpoint: GET api/admin/shipment/{hawb}
 *
 * Expected Response:
 * {
 *   success: boolean,
 *   data: {
 *     hawb: string,
 *     company_name: string,
 *     origin: string,
 *     shipper_address: string,
 *     weight: string,
 *     current_route: string,
 *     current_driver: string,
 *     current_status: { status, timestamp, updated_by, role },
 *     events: [{
 *       id: number,
 *       status: string,
 *       timestamp: string (ISO),
 *       location?: string,
 *       reason?: string,
 *       driver_name?: string,
 *       old_route?: string,
 *       new_route?: string,
 *       updated_by: string,
 *       role: string,
 *       pod_url?: string,  // for DELIVERED
 *       pofd_url?: string  // for FAILED DELIVERY
 *     }]
 *   }
 * }
 */

export const ShipmentTracking = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hawb, setHawb] = useState("");
  const [shipmentData, setShipmentData] = useState(null);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" = Latest First, "asc" = Oldest First
  const [hasSearched, setHasSearched] = useState(false);
  const [photoModalVisible, setPhotoModalVisible] = useState(false);
  const [photoModalUrl, setPhotoModalUrl] = useState(null);
  const [btnLoadingPDF,setBtnLoadingPDF]=useState(false);

  const handleSearch = async (values) => {
    const searchHawb = (values?.hawb || hawb || "").trim();
    if (!searchHawb) return;

    setLoading(true);
    setError(null);
    setShipmentData(null);
    setHasSearched(true);

    try {
      const response = await getRequest(`package-history/?hawb=${searchHawb}`);
      console.log("response", response?.data?.data);
      if (response?.data) {
        setShipmentData(response.data.data);
        setError(null);
      } else {
        setShipmentData(null);
        setError("Shipment not found. Please verify HAWB number.");
        // ErrorNotificationMsg("Shipment not found. Please verify HAWB number.");
      }
    } catch (err) {
      // Use mock data when API is not available - remove this block when backend is ready
      // const mockData = MOCK_SHIPMENT_RESPONSE.data
      //   ? { ...MOCK_SHIPMENT_RESPONSE.data, hawb: searchHawb, parcel_no: searchHawb }
      //   : null;
      // setShipmentData(mockData);
      // setError(null);
      // if (!mockData) {
      //   ErrorNotificationMsg("Shipment not found. Please verify HAWB number.");
      // }
       setError("Shipment not found. Please verify HAWB number.");
        // ErrorNotificationMsg("Shipment not found. Please verify HAWB number.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setHawb("");
    setShipmentData(null);
    setError(null);
    setHasSearched(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      form.submit();
    }
  };

const handleDownloadPODURL = (url) => {
  console.log("url",url);
  if (url) window.open(url, "_blank");
};
    const handleDownloadPOD = async (id,attemptNumber) => {
      if (!id) {
        console.error("No ID provided for downloading the pdf.");
        return;
      }
      setBtnLoadingPDF(true);
      try {
        // Construct the API endpoint using the id
        const endpoint = `package-model/${id}/pod-download-by-package-id/?attempt_number=${attemptNumber}`;
  
        // Send a GET request to the endpoint
        const response = await getRequest(endpoint);
  
        // Process and download the pdf file
        const blob = new Blob([response.data], { type: "text/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Package_List_${Date.now()}.pdf`;
        link.click();
        link.remove(); // Clean up the DOM element
        setBtnLoadingPDF(false);
      } catch (error) {
        setBtnLoadingPDF(false);
        if (error.response && error.response.status === 404) {
          console.error("File not found or endpoint is incorrect.");
          ErrorNotificationMsg(t("file_not_found"));
          setBtnLoadingPDF(false);
        } else {
          console.error("Error downloading the file:", error);
          ErrorNotificationMsg(t("error_downloading_file"));
          setBtnLoadingPDF(false);
        }
      }
    };

  const handleDownloadPOFD = async (id,attemptNumber) => {
      if (!id) {
        console.error("No ID provided for downloading the pdf.");
        return;
      }
      setBtnLoadingPDF(true);
      try {
        // Construct the API endpoint using the id
        const endpoint = `package-model/${id}/pod-download-by-package-id/?attempt_number=${attemptNumber}`;
  
        // Send a GET request to the endpoint
        const response = await getRequest(endpoint);
  
        // Process and download the pdf file
        const blob = new Blob([response.data], { type: "text/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `Package_List_${Date.now()}.pdf`;
        link.click();
        link.remove(); // Clean up the DOM element
        setBtnLoadingPDF(false);
      } catch (error) {
        setBtnLoadingPDF(false);
        if (error.response && error.response.status === 404) {
          console.error("File not found or endpoint is incorrect.");
          ErrorNotificationMsg(t("file_not_found"));
          setBtnLoadingPDF(false);
        } else {
          console.error("Error downloading the file:", error);
          ErrorNotificationMsg(t("error_downloading_file"));
          setBtnLoadingPDF(false);
        }
      }
    };

  const getSortedEvents = () => {
    const events = shipmentData?.events || shipmentData?.history || shipmentData?.timeline || [];
    if (!Array.isArray(events) || events.length === 0) return [];
    // Keep backend sequence as source-of-truth (workflow order),
    // and only flip it for timeline direction toggle.
    const orderedEvents = [...events];
    return sortOrder === "asc" ? orderedEvents : orderedEvents.reverse();
  };

  const getFailDeliveryFromEvent = (e) => {
    const first = (e.photo_urls || [])[0];
    return first && first.fail_delivery === true;
  };

  /**
   * Collapse consecutive PHOTO UPLOADED events with same delivery_attempt AND same fail_delivery into one display item.
   * Returns array of { type: 'event', event } | { type: 'photo_group', ... }.
   */
  const getDisplayEvents = () => {
    const sorted = getSortedEvents();
    if (sorted.length === 0) return [];

    const display = [];
    const isPhotoUploaded = (e) =>
      (e.status || "").toUpperCase().trim() === "PHOTO UPLOADED";

    for (let i = 0; i < sorted.length; i++) {
      const event = sorted[i];
      if (!isPhotoUploaded(event)) {
        display.push({ type: "event", event });
        continue;
      }

      const attemptId = event.delivery_attempt?.id;
      const attemptNumber = event.delivery_attempt?.attempt_number;
      const failDelivery = getFailDeliveryFromEvent(event);
      // Copy array so we never mutate original event.photo_urls
      const photoUrls = [...(event.photo_urls || [])];
      const firstTs = event.timestamp || event.created_at || event.date;
      const driverName = event.driver_name || event.driver || event.assigned_to;
      const updatedBy = event.updated_by || event.updatedBy;
      const role = event.role || event.user_role;

      let j = i + 1;
      while (j < sorted.length && isPhotoUploaded(sorted[j])) {
        const next = sorted[j];
        const nextFailDelivery = getFailDeliveryFromEvent(next);
        if (
          next.delivery_attempt?.id === attemptId &&
          nextFailDelivery === failDelivery
        ) {
          (next.photo_urls || []).forEach((p) => photoUrls.push(p));
          j++;
        } else break;
      }

      const filteredPhotoUrls = photoUrls.filter((p) => p && p.photo_url);
      const firstPhoto = filteredPhotoUrls[0] || {};
      const otherReasons = firstPhoto.other_reasons || null;
      const deliveryIssues = Array.isArray(firstPhoto.delivery_issues)
        ? firstPhoto.delivery_issues
        : [];

      display.push({
        type: "photo_group",
        attemptNumber: attemptNumber || "—",
        attemptId,
        failDelivery,
        timestamp: firstTs,
        driver_name: driverName,
        updated_by: updatedBy,
        role,
        photoUrls: filteredPhotoUrls,
        otherReasons,
        deliveryIssues,
      });
      i = j - 1;
    }

    return display;
  };

  const openPhotoModal = (url) => {
    setPhotoModalUrl(url);
    setPhotoModalVisible(true);
  };

  const closePhotoModal = () => {
    setPhotoModalVisible(false);
    setPhotoModalUrl(null);
  };

  const renderTimelineEvent = (event) => {
    const status = event.status || event.event_type || event.type || "Unknown";
    const timestamp = event.timestamp || event.created_at || event.date;
    const location = event.location || event.address || event.place;
    const reason = event.reason || event.remarks || event.comment;
    const driverName = event.driver_name || event.driver || event.assigned_to;
    const oldRoute = event.old_route || event.previous_route;
    const newRoute = event.new_route || event.route_name || event.route;
    const updatedBy = event.updated_by || event.updatedBy;
    const role = event.role || event.user_role;
    const updatedByDisplay = role && updatedBy ? `${role} - ${updatedBy}` : updatedBy || "-";
    const podUrl = event?.pod_url || event?.pod;
    const pofdUrl = event.pofd_url || event.pofd;
    const isFailedDelivery =
      (status || "").toUpperCase().trim() === "FAILED DELIVERY" ||
      (status || "").trim() === "Failed Delivery";
    const attemptNumber = event.delivery_attempt?.attempt_number;

    const isSignatureUploaded = (status || "").toUpperCase().trim() === "SIGNATURE UPLOADED";
    const signatureUrls = event.signature_urls || [];
    const firstSignature = signatureUrls[0];
    const signatureUrl = firstSignature?.signature_url;
    const signaturePersonName =
      event.signature_person_name ?? firstSignature?.signature_person_name ?? null;

    return (
      <Timeline.Item key={event.id || JSON.stringify(event)}>
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <Tag color={getStatusTagColor(isSignatureUploaded ? "SIGNATURE UPLOADED" : status)}>
                {isSignatureUploaded ? "SIGNATURE UPLOADED FOR DELIVERED" : status}
                {isFailedDelivery && attemptNumber != null && attemptNumber !== "" && (
                  <span style={{ marginLeft: 4 }}>(Attempt {attemptNumber})</span>
                )}
              </Tag>
              {timestamp && <Text type="secondary">{formatDateTime(timestamp)}</Text>}
            </div>
          
            {isSignatureUploaded && signatureUrl && (
              <div>
                <Text strong>Signature: </Text>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => openPhotoModal(signatureUrl)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openPhotoModal(signatureUrl);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    display: "inline-block",
                    marginTop: 4,
                    borderRadius: 6,
                    overflow: "hidden",
                    border: "1px solid #d9d9d9",
                    background: "#fafafa",
                  }}
                  title="View signature"
                >
                  <img
                    src={signatureUrl}
                    alt="Signature"
                    style={{
                      width: 120,
                      height: 56,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            )}
              {isSignatureUploaded && signaturePersonName && (
              <div>
                <Text strong>Signature By: </Text>
                <Text>{signaturePersonName}</Text>
              </div>
            )}
            {location && (
              <div>
                <Text strong>Location: </Text>
                <Text>{location}</Text>
              </div>
            )}
            {reason && (
              <div>
                <Text strong>Reason: </Text>
                <Text>{reason}</Text>
              </div>
            )}
            {driverName && (
              <div>
                <Text strong>{role==="Super Admin" ? "Driver":role}: </Text>
                <Text>{driverName}</Text>
              </div>
            )}
            {(oldRoute || newRoute) && (
              <div>
                {oldRoute && (
                  <span>
                    <Text strong>Old Route: </Text>
                    <Text>{oldRoute}</Text>
                    {newRoute && " → "}
                  </span>
                )}
                {newRoute && (
                  <span>
                    <Text strong>New Route: </Text>
                    <Text>{newRoute}</Text>
                  </span>
                )}
              </div>
            )}
            <div>
              <Text strong>Updated By: </Text>
              <Text type="secondary">{updatedByDisplay}</Text>
            </div>
            {/* {(podUrl || pofdUrl) && ( */}
              <Space>
                {status?.toUpperCase() === "DELIVERED"  && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPOD(shipmentData?.id,attemptNumber)}
                       loading={btnLoadingPDF}
                  >
                    Download POD
                  </Button>
                )}
                {status?.toUpperCase() === "FAILED DELIVERY"  && (
                  <Button
                    type="primary"
                    size="small"
                    danger
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPOFD(shipmentData?.id,attemptNumber)}
                  >
                    Download POFD
                  </Button>
                )}
                  {status?.toUpperCase() === "STATUS UPDATE FROM TRACKING EVENT - DELIVERED"  && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadPODURL(podUrl)}
                       loading={btnLoadingPDF}
                  >
                    Download POD
                  </Button>
                )}
              </Space>
            {/* )} */}
          </Space>
        </Card>
      </Timeline.Item>
    );
  };

  const renderPhotoGroup = (item, index) => {
    const {
      attemptNumber,
      failDelivery,
      timestamp,
      driver_name,
      updated_by,
      role,
      photoUrls,
      otherReasons,
      deliveryIssues,
    } = item;
    const updatedByDisplay = role && updated_by ? `${role} - ${updated_by}` : updated_by || "-";
    const photoTitle = failDelivery
      ? `PHOTO UPLOADED FOR FAILED DELIVERY (ATTEMPT ${attemptNumber})`
      : "PHOTO UPLOADED FOR DELIVERED";
    return (
      <Timeline.Item key={`photo-group-${item.attemptId}-${item.failDelivery}-${index}`}>
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
              <Tag color={getStatusTagColor("PHOTO UPLOADED")}>
                {photoTitle}
              </Tag>
              {timestamp && <Text type="secondary">{formatDateTime(timestamp)}</Text>}
            </div>
            {driver_name && (
              <div>
                <Text strong>{role || "Driver"}: </Text>
                <Text>{driver_name}</Text>
              </div>
            )}
            <div>
              <Text strong>Updated By: </Text>
              <Text type="secondary">{updatedByDisplay}</Text>
            </div>
            {otherReasons && (
              <div>
                <Text strong>Other Reasons: </Text>
                <Text>{otherReasons}</Text>
              </div>
            )}
            {deliveryIssues && deliveryIssues.length > 0 && (
              <div>
                <Text strong>Delivery Issues: </Text>
                <Text>{deliveryIssues.join(", ")}</Text>
              </div>
            )}
            {photoUrls.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
                {photoUrls.map((photo, idx) => (
                  <div
                    key={photo.id || idx}
                    role="button"
                    tabIndex={0}
                    onClick={() => openPhotoModal(photo.photo_url)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openPhotoModal(photo.photo_url);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      textAlign: "center",
                      flexShrink: 0,
                    }}
                    title={`View Photo ${idx + 1}`}
                  >
                    <div
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 6,
                        overflow: "hidden",
                        border: "1px solid #d9d9d9",
                        marginBottom: 4,
                        background: "#fafafa",
                      }}
                    >
                      <img
                        src={photo.photo_url}
                        alt={`Attempt ${attemptNumber} thumbnail ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Photo {idx + 1}
                    </Text>
                  </div>
                ))}
              </div>
            )}
          </Space>
        </Card>
      </Timeline.Item>
    );
  };

  const currentStatus = shipmentData?.current_status || shipmentData?.status;
  const currentStatusData =
    typeof currentStatus === "object"
      ? currentStatus
      : { status: currentStatus, timestamp: null, updated_by: null };

  return (
    <>
      {/* Page Header */}
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>{t("Shipment Tracking")}</h3>
            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/dashboard">{t("home")}</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("Shipment Tracking")}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
      </div>

      {/* Main Section */}
      <section className="grid-sec">
        <div className="Package-body">
          <div className="filterrow packagefilter">
            <Content className="content-section">
            {/* Search Section */}
            {/* <Card title="Search Shipment" style={{ marginBottom: 24 }}> */}
              <Form
                form={form}
                onFinish={handleSearch}
                layout="vertical"
                initialValues={{ hawb: "" }}
              >
                <Row gutter={16} align="top">
                  <Col xs={24} sm={16} md={12} lg={10}>
                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const hawbErrors = form.getFieldError("hawb") || [];
                        return (
                          <Form.Item
                            label="Enter HAWB Number"
                            name="hawb"
                            rules={[
                              {
                                required: true,
                                message: "Please enter HAWB number",
                              },
                            ]}
                            validateStatus={hawbErrors.length ? "error" : undefined}
                            help={
                              hawbErrors[0] || (
                                <span style={{ visibility: "hidden" }}>
                                  Please enter HAWB number
                                </span>
                              )
                            }
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              placeholder="Enter HAWB Number"
                              size="large"
                              onKeyDown={handleKeyDown}
                              onChange={(e) => setHawb(e.target.value || "")}
                              disabled={loading}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8} md={12} lg={14}>
                    <Form.Item label=" " style={{ marginBottom: 0 }}>
                      <Space>
                        <Button
                          type="default"
                          icon={<CloseOutlined />}
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Clear
                        </Button>
                        <ButtonField
                          className="ant-btn ant-btn-primary save-gradient-btn SaveGradiantBtn"
                          type="primary"
                          htmlType="submit"
                          label="Search"
                          icon={<SearchOutlined />}
                          disabled={loading}
                        />
                      </Space>
                    </Form.Item>
                    <div style={{ minHeight: 22, marginTop: 4, visibility: "hidden" }}>
                      Please enter HAWB number
                    </div>
                  </Col>
                </Row>
              </Form>

              {loading && (
                <div style={{ textAlign: "center", padding: 24 }}>
                  <Spin size="large" tip="Loading shipment..." />
                </div>
              )}

              {!loading && !hasSearched && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Please enter a HAWB number to view shipment history."
                  style={{ padding: "40px 0" }}
                />
              )}

              {!loading && hasSearched && error && !shipmentData && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Shipment not found. Please verify HAWB number."
                  style={{ padding: "40px 0" }}
                />
              )}
            {/* </Card> */}

            {/* Shipment Summary & Timeline - Show only when data exists */}
            {shipmentData && !loading && (
              <>
                {/* Shipment Summary Section */}
                <Card title="Shipment Summary" style={{ marginBottom: 24 }}>
                  <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }} size="small">
                    <Descriptions.Item label="HAWB">
                      {shipmentData.hawb || shipmentData.parcel_no || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Created In System">{shipmentData.created_at||""}</Descriptions.Item>
                      <Descriptions.Item label="Order No.">{shipmentData.order_no||"-"}</Descriptions.Item>
                    <Descriptions.Item label="Company Name">
                      {shipmentData.company_name || shipmentData.company || "-"}
                    </Descriptions.Item>
                    
                
                    <Descriptions.Item label="Customer Name">
                      {shipmentData.customer_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Customer Phone">  {shipmentData.customer_phone || "-"} </Descriptions.Item>
                    <Descriptions.Item label="Customer Email">  {shipmentData.customer_email || "-"} </Descriptions.Item>
                    <Descriptions.Item label="Customer Code">  {shipmentData.customer_code || "-"} </Descriptions.Item>
                    <Descriptions.Item label="Customer Address">
                      {shipmentData.customer_address || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Suburb">
                      {shipmentData.suburb_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Postcode">
                      {shipmentData.postcode_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="State">
                      {shipmentData.state_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Country">
                      {shipmentData.country_name || "-"}
                    </Descriptions.Item>
                  
                    <Descriptions.Item label="Run">
                      {shipmentData.zone_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Zone">
                      {shipmentData.customer_zone_name || "-"}
                    </Descriptions.Item>
                    


                 
                    <Descriptions.Item label="Shipper Name">
                      {shipmentData.shipper_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Shipper Address">
                      {shipmentData.shipper_address || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Origin">
                      {shipmentData.origin || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Weight">
                      {shipmentData.weight || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Price">
                      {shipmentData.price || "-"}

                    </Descriptions.Item>
                    <Descriptions.Item label="Description of Goods">
                      {shipmentData.description_of_goods || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Special Instructions">
                      {shipmentData.special_instructions || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Delivery Instructions">
                      {shipmentData.delivery_instructions || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Height">
                      {shipmentData.height || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Length">
                      {shipmentData.length || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Width">
                      {shipmentData.width || "-"}
                    </Descriptions.Item>
                   
                   
                    <Descriptions.Item label="Current Route">
                      {shipmentData.current_route || shipmentData.route_name || "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Current Driver" span={2}>
                      {shipmentData.current_driver ||
                        shipmentData.assigned_to ||
                        shipmentData.driver_name ||
                        "-"}
                    </Descriptions.Item>
                  </Descriptions>

                  <Divider />

                  <div>
                    <Text strong style={{ marginRight: 8 }}>Current Status:</Text>
                    <Tag color={getStatusTagColor(currentStatusData?.status || currentStatus)}>
                      {currentStatusData?.status || currentStatus || "-"}
                    </Tag>
                    {currentStatusData?.timestamp && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        {formatDateTime(currentStatusData.timestamp)}
                      </Text>
                    )}
                    {currentStatusData?.updated_by && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary">
                          Updated By: {currentStatusData.role && currentStatusData.updated_by
                            ? `${currentStatusData.role} - ${currentStatusData.updated_by}`
                            : currentStatusData.updated_by}
                        </Text>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Operational Timeline Section */}
                <Card
                  title="Operational Timeline"
                  extra={
                    <Space>
                      <Text type="secondary">Oldest First</Text>
                      <Switch
                        checked={sortOrder === "desc"}
                        onChange={(checked) => setSortOrder(checked ? "desc" : "asc")}
                      />
                      <Text type="secondary">Latest First</Text>
                    </Space>
                  }
                  style={{ marginBottom: 24 }}
                >
                  <Timeline mode="left">
                    {getDisplayEvents().map((item, index) =>
                      item.type === "photo_group"
                        ? renderPhotoGroup(item, index)
                        : renderTimelineEvent(item.event)
                    )}
                  </Timeline>
                  {getDisplayEvents().length === 0 && (
                    <Empty description="No events found" style={{ padding: 24 }} />
                  )}
                </Card>
              </>
            )}
            </Content>
          </div>
        </div>
      </section>

      <Modal
        title="View Photo"
        open={photoModalVisible}
        onCancel={closePhotoModal}
        footer={null}
        width="auto"
        centered
        destroyOnClose
      >
        {photoModalUrl && (
          <img
            src={photoModalUrl}
            alt="Delivery evidence"
            style={{ maxWidth: "100%", height: "auto", display: "block" }}
          />
        )}
      </Modal>
    </>
  );
};

export default ShipmentTracking;
